import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from 'src/contexts/SessionContext';
import { fetchAllTrainers } from 'src/models/trainer/trainer';
import { fetchUsers } from 'src/models/user/user';
import { getUserDisplayName } from 'src/models/user/utils';
import { Identifier } from 'src/types/utilityTypes';

import {
  BaseSelect,
  BaseSelectOption,
  FormSelectProps,
  SelectProps,
  createFormSelect,
} from './BaseSelect';

type TrainerSelectProps = Omit<SelectProps, 'options' | 'loading'> & {
  includeIds?: Identifier[];
  multiple?: boolean;
  filterOptions?: (
    options: BaseSelectOption[],
    { inputValue }: { inputValue: string }
  ) => BaseSelectOption[];
  onInit?: (options: BaseSelectOption[]) => void;
};

export const TrainerSelect = ({
  includeIds,
  multiple,
  filterOptions,
  onInit,
  ...props
}: TrainerSelectProps) => {
  const { t } = useTranslation('translation');
  const { sessionTrainer } = useSession();
  const initialRef = useRef(false);

  const { data: trainers, isLoading } = useQuery({
    queryKey: ['trainers_select', 'all', includeIds],
    staleTime: 60000,
    gcTime: 60000,
    queryFn: async () => {
      const activeTrainers = await fetchAllTrainers();
      if (!includeIds?.length) return activeTrainers;

      const activeIds = new Set(activeTrainers.map((trainer) => String(trainer.trainerId)));
      const missingIds = includeIds.filter(
        (id) =>
          !activeIds.has(String(id)) &&
          String(id) !== String(sessionTrainer?.id)
      );
      if (!missingIds.length) return activeTrainers;

      const archivedUsers = await fetchUsers({ ids: missingIds });
      return [
        ...activeTrainers,
        ...archivedUsers
          .map((user) => ({
            name: getUserDisplayName(user),
            email: user.email,
            trainerId: user.id,
            archivedAt: new Date(),
            disabled: true,
          })),
      ];
    },
  });

  const options: BaseSelectOption[] = (trainers || []).map((trainer) => ({
    id: trainer.trainerId,
    label: `${trainer.name}${trainer.archivedAt ? ` (${t('staff.archived')})` : ''}`,
    secondary: trainer.email,
    locationId: 'locationId' in trainer ? trainer.locationId : undefined,
    disabled: 'disabled' in trainer ? trainer.disabled : false,
  }));

  if (sessionTrainer && !options.some((o) => o.id === sessionTrainer.id)) {
    const trainerOption = {
      id: sessionTrainer.id,
      label: getUserDisplayName(sessionTrainer) || '',
      secondary: sessionTrainer.email,
      locationId: sessionTrainer.locationId,
    };
    options.unshift(trainerOption);
  }

  useEffect(() => {
    if (isLoading) return;
    if (options && !initialRef.current) {
      onInit?.(options);
      initialRef.current = true;
    }
  }, [onInit, options, isLoading]);

  return (
    <BaseSelect
      {...props}
      multiple={multiple}
      options={options}
      filterOptions={(availableOptions, state) => {
        const activeOptions = availableOptions.filter((option) => !option.disabled);
        return filterOptions
          ? filterOptions(activeOptions, state)
          : activeOptions.filter((option) =>
              option.label.toLowerCase().includes(state.inputValue.toLowerCase())
            );
      }}
      loading={isLoading}
    />
  );
};

type FormTrainerSelectProps = FormSelectProps & Pick<TrainerSelectProps, 'includeIds'>;

export const FormTrainerSelect = createFormSelect<FormTrainerSelectProps>(TrainerSelect);
